import { Button, DialogDescription, Input, Label } from "@medimo/ui";
import { Check, Copy, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import type React from "react";
import type { TOTPSetupData } from "../../hooks/use-totp-setup";

interface SetupStepProps {
  setupData: TOTPSetupData | null;
  isLoading: boolean;
  secretCopied: boolean;
  email: string;
  disabled?: boolean;
  onCopySecret: () => void;
  onNext: () => void;
  onCancel: () => void;
}

export const SetupStepContent = ({
  setupData,
  isLoading,
  secretCopied,
  email,
  disabled = false,
  onCopySecret,
  onNext,
  onCancel,
}: SetupStepProps) => (
  <div className="space-y-4">
    {isLoading && (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="my-auto h-4 w-4 animate-spin" />
        <p>ローディング中</p>
      </div>
    )}
    {!isLoading && setupData && (
      <>
        <p className="text-gray-600 text-sm">
          認証アプリ（Google
          Authenticator、Authy等）で二次元コード読み取り、認証の設定を行ってください。
          <span className="font-bold">
            １つのアカウントに対して登録できるデバイスは１つのみです。
          </span>
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="font-medium text-sm">
              セットアップ2次元コード（カメラ入力用）：
            </Label>
            <div className="flex justify-center">
              <QRCodeSVG
                value={setupData.getSetupUri("Medimo", email).toString()}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="font-medium text-sm">
              シークレットキー（手動入力用）：
            </Label>
            <div className="flex items-center gap-2">
              <Input
                value={setupData.sharedSecret}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                variant="outline"
                onClick={onCopySecret}
                className="flex items-center gap-1"
              >
                {secretCopied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                コピー
              </Button>
            </div>
          </div>

          <div className="rounded-lg bg-blue-50 p-4">
            <p className="mb-1 font-semibold text-sm">設定手順:</p>
            <p className="mb-2 text-sm">
              ※以下は一例です。詳しくはお使いの認証アプリの設定手順をご確認ください。
            </p>
            <ol className="list-outside list-decimal space-y-1 pl-4 text-gray-600 text-sm">
              <li>認証アプリを開きます。</li>
              {/* no typography installed; use manual css */}
              <li style={{ textIndent: "-0.5rem" }}>
                「アカウントを追加」または「+」ボタンをタップします。
              </li>
              <li style={{ textIndent: "-0.5rem" }}>
                「セットアップキーを入力」か「QRコードをスキャン」を選択します。
              </li>
              <li>アカウント名:に「medimo」と入力します。</li>
              <li>
                手動で入力する場合は、上記のシークレットキーを入力します。
              </li>
              <li style={{ textIndent: "-0.5rem" }}>
                「追加」をタップします。
              </li>
              <li>
                ワンタイムパスワードが表示されたら以下の「次へ」ボタンを押して、認証アプリに表示されている6桁のコードを入力してください。
              </li>
            </ol>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            className="w-full"
            onClick={onCancel}
            variant="outline"
            disabled={disabled}
          >
            キャンセル
          </Button>
          <Button
            className="w-full"
            onClick={onNext}
            disabled={isLoading || disabled}
          >
            次へ
          </Button>
        </div>
      </>
    )}
  </div>
);

interface VerifyStepProps {
  verificationCode: string;
  verificationError: string | null;
  isVerifying: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onCodeChange: (code: string) => void;
  onBack: () => void;
  onVerify: () => void;
}

export const VerifyStepContent = ({
  verificationCode,
  verificationError,
  isVerifying,
  inputRef,
  onCodeChange,
  onBack,
  onVerify,
}: VerifyStepProps) => (
  <div className="space-y-4">
    <DialogDescription className="text-gray-600 text-sm">
      認証アプリに表示されている6桁のコードを入力してください。
    </DialogDescription>
    <div className="space-y-2">
      <Label htmlFor="verification-code">認証コード</Label>
      <Input
        ref={inputRef}
        id="verification-code"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        placeholder="000000"
        value={verificationCode}
        onChange={(e) => onCodeChange(e.target.value)}
        className="text-center font-mono text-lg tracking-widest"
        autoComplete="one-time-code"
        spellCheck="false"
        data-form-type="other"
      />
      {verificationError && (
        <div className="text-red-600 text-sm">{verificationError}</div>
      )}
    </div>
    <div className="flex gap-2">
      <Button
        variant="outline"
        onClick={onBack}
        disabled={isVerifying}
        className="flex-1"
      >
        戻る
      </Button>
      <Button
        className="flex-1"
        onClick={onVerify}
        disabled={isVerifying || verificationCode.length !== 6}
      >
        {isVerifying ? (
          <>
            <Loader2 className="my-auto h-4 w-4 animate-spin" /> 認証中
          </>
        ) : (
          "認証"
        )}
      </Button>
    </div>
  </div>
);

interface CompleteStepProps {
  onSetupComplete: () => void;
}

export const CompleteStepContent = ({ onSetupComplete }: CompleteStepProps) => (
  <div className="space-y-4">
    <div className="py-4 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
        <Check className="h-8 w-8 text-green-600" />
      </div>
      <div className="mb-2 font-medium text-lg">
        ワンタイムパスワードが有効になりました
      </div>
      <DialogDescription className="text-gray-600 text-sm">
        次回ログイン時から、認証アプリで生成されるコードが必要になります。
      </DialogDescription>
    </div>
    <Button
      className="w-full"
      onClick={() => {
        onSetupComplete();
      }}
    >
      完了
    </Button>
  </div>
);
